import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, LogOut, User as UserIcon } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/design-system/lib/utils";
import { NotificationBell } from "@/components/ux";
import { MessagesBadge } from "@/components/messaging/MessagesBadge";
import { CommunicationsNavIcon } from "@/components/communications/CommunicationsNavIcon";
import { WhosCallingNavButton } from "@/components/buyers/WhosCallingPanel";
import { RoleSwitcher } from "@/dashboard/RoleSwitcher";

interface TopBarProps {
  onHamburger: () => void;
  hamburgerIcon: React.ReactNode;
}

export function TopBar({ onHamburger, hamburgerIcon }: TopBarProps) {
  const { user, primaryRoleCode, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the dropdown when the user clicks elsewhere or presses Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  return (
    <header
      className={cn(
        "sticky top-0 z-30 h-16 flex items-center gap-space-3 px-space-3 md:px-space-5",
        "bg-onyx text-white-pure border-b border-border-dark"
      )}
    >
      <button
        type="button"
        className={cn(
          "md:hidden inline-flex items-center justify-center h-10 w-10 rounded-md",
          "text-white-pure/80 hover:text-white-pure hover:bg-white-pure/5"
        )}
        onClick={onHamburger}
        aria-label="Open navigation"
      >
        {hamburgerIcon}
      </button>

      <div className="flex-1" />

      <RoleSwitcher />

      <NotificationBell />
      <MessagesBadge />
      <CommunicationsNavIcon />
      <WhosCallingNavButton />

      <div className="relative" ref={menuRef}>
        <button
          type="button"
          className={cn(
            "flex items-center gap-space-2 h-10 pl-space-2 pr-space-3 rounded-md",
            "text-white-pure/80 hover:text-white-pure hover:bg-white-pure/5"
          )}
          onClick={() => setMenuOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <Avatar name={user?.displayName ?? "?"} avatarUrl={user?.avatarUrl} />
          <div className="hidden sm:flex flex-col items-start leading-tight text-left">
            <span className="text-small font-semibold text-white-pure">
              {user?.displayName ?? "Guest"}
            </span>
            <span className="text-label text-white-pure/60 font-display uppercase tracking-label">
              {primaryRoleCode ?? "—"}
            </span>
          </div>
        </button>

        {menuOpen && (
          <div
            className={cn(
              "absolute right-0 mt-space-2 w-56 rounded-md shadow-lg z-50",
              "border border-border-light bg-white-pure text-onyx"
            )}
            role="menu"
          >
            <div className="px-space-3 py-space-3 border-b border-border-light">
              <div className="text-small font-semibold text-onyx truncate">
                {user?.displayName}
              </div>
              <div className="text-label text-gray-mid truncate">{user?.email}</div>
            </div>
            <Link
              to="/account"
              onClick={() => setMenuOpen(false)}
              className="w-full flex items-center gap-space-2 px-space-3 py-space-2 text-small hover:bg-white-warm"
              role="menuitem"
            >
              <KeyRound size={16} strokeWidth={1.5} aria-hidden /> Account
            </Link>
            <Link
              to="/account/profile"
              onClick={() => setMenuOpen(false)}
              className="w-full flex items-center gap-space-2 px-space-3 py-space-2 text-small hover:bg-white-warm"
              role="menuitem"
            >
              <UserIcon size={16} strokeWidth={1.5} aria-hidden /> My profile
            </Link>
            <div className="border-t border-border-light" />
            <button
              type="button"
              onClick={() => void logout()}
              className="w-full flex items-center gap-space-2 px-space-3 py-space-2 text-small hover:bg-white-warm"
              role="menuitem"
            >
              <LogOut size={16} strokeWidth={1.5} aria-hidden /> Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

function Avatar({ name, avatarUrl }: { name: string; avatarUrl?: string | null }) {
  if (avatarUrl) {
    return <img src={avatarUrl} alt="" className="h-8 w-8 rounded-pill object-cover" />;
  }
  const initials = name
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <span className="h-8 w-8 rounded-pill bg-gold text-onyx flex items-center justify-center text-label font-display font-bold">
      {initials || "?"}
    </span>
  );
}
